import styled from 'styled-components';

import { FormBuilder } from '@components/form-builder';
import { useFormBuilderContext } from '@components/form-builder/context';
import { ElementsList } from '@components/form-builder/elements-list';
import { getFieldConfigForKind } from '@components/form-builder/field-registry';
import { GroupAccordion } from '@components/form-builder/group-accordion';
import { BuilderProps, FieldType, FieldTypeKind, FormV2, InputKind } from '@components/form/v2';
import { useDeleteModal } from '@components/generic-modals';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconAttributeJourney } from '@components/icons/attribute-journey';
import { IconAttributeTraveller } from '@components/icons/attribute-traveller';
import { IconDecimalFloat } from '@components/icons/decimal-float';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconSettings } from '@components/icons/settings';
import { IconShare } from '@components/icons/share';
import { IconTrash } from '@components/icons/trash';
import { Spinner } from '@components/loading-spinner';
import { Menu } from '@components/menu';
import { hideOverlay, useOverlay } from '@components/overlay';
import { Stack } from '@components/stack';
import { Switch } from '@components/switch';
import { showError } from '@components/toaster';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { useCompanyTravellerAttributeTemplates } from 'api/attributes/hooks';
import {
  AttributeFieldType,
  AttributeType,
  TravellerAttributeTemplate,
} from 'api/attributes/requests';
import {
  useCreateJourneyFormImage,
  useDeleteJourneyForm,
  useJourneyForm,
  usePublishJourneyForm,
  useUnpublishJourneyForm,
  useUpdateJourneyForm,
} from 'api/journey-forms/hooks';
import { JourneyForm } from 'api/journey-forms/requests';
import { useCreateAttribute, useJourney, useJourneyAttributes } from 'api/journeys/hooks';
import { JourneyAttributeData } from 'api/journeys/requests';
import { useFormBuilderLists } from 'api/lists/hooks';
import { FavoriteMenuItem } from 'ui/authenticated/main/favorites/favorites';
import { useShareJourneyFormModal } from 'ui/authenticated/main/pages/journeys/share-journey-form';
import { ShareJourneyFormButton } from 'ui/authenticated/main/pages/journeys/share-journey-form-button';
import { useAddAttributeSidebar } from 'ui/authenticated/settings/pages/attributes/sidebars/form';
import { useFeature } from 'ui/hooks';

import { JourneyFormSettingsPage } from './settings-tab';
import { attributeTypeToFieldType } from './use-create-default-journey-form';

export const useEditJourneyFormOverlay = () => {
  return useOverlay(EditJourneyFormModal);
};

const EditJourneyFormModal = ({
  journeyId,
  formId,
  onClose,
}: {
  journeyId: string;
  formId: string;
  onClose?: () => void;
}) => {
  const { t } = useScopedTranslate(locale);
  const { isEnabled: hasKennitalaEnabled } = useFeature('FEATURE_KENNITALA');
  const formQuery = useJourneyForm(journeyId, formId);
  const journeyQuery = useJourney(journeyId);
  const { data: lists, isLoading: listsLoading } = useFormBuilderLists();
  const updateMutation = useUpdateJourneyForm(formId);
  const createFormTemplateImageQuery = useCreateJourneyFormImage();
  const ctaQuery = useCompanyTravellerAttributeTemplates({ systemAttributes: true });
  const jtaQuery = useJourneyAttributes(journeyId);

  if (
    formQuery.isLoading ||
    listsLoading ||
    journeyQuery.isLoading ||
    ctaQuery.isLoading ||
    jtaQuery.isLoading
  ) {
    return <Spinner />;
  }

  if (formQuery.isError) {
    showError(t('formNotFound'));
    onClose?.();
    hideOverlay();
    return null;
  }

  if (
    !formQuery.isSuccess ||
    !journeyQuery.isSuccess ||
    !ctaQuery.isSuccess ||
    !jtaQuery.isSuccess
  ) {
    return <Spinner />;
  }

  const extraTabs = [
    {
      section: 'settings',
      label: t('settings'),
      content: (
        <JourneyFormSettingsPage
          form={formQuery.data}
          isAutoStartOnChange={(isAutoStart) => {
            updateMutation.mutate({
              ...formQuery.data,
              isAutoStart,
            });
          }}
          portalOnChange={(isPortalForm) => {
            updateMutation.mutate({
              ...formQuery.data,
              isPortalForm,
            });
          }}
        />
      ),
      icon: <IconSettings />,
    },
  ];

  const journeyForm = formQuery.data;

  // Process form fields to apply proper builderProps based on attributes
  // The API should do this correctly from now on but we can do it here just in case
  const processFormFields = (form: FormV2): FormV2 => {
    const processField = (field: FieldType): FieldType => {
      let updatedField = { ...field };

      // Process nested fields in sections
      if (field.kind === 'section' && field.fields) {
        (updatedField as typeof field).fields = field.fields.map(processField);
      }

      // Apply builderProps for fields with journey attributes
      if ('journeyAttributeId' in field && field.journeyAttributeId) {
        const attribute = jtaQuery.data.find((attr) => attr.shortId === field.journeyAttributeId);
        if (attribute) {
          updatedField.builderProps = {
            ...field.builderProps,
            canEditKind: attributeTypeToAllowedFieldTypes[attribute.attributeType],
          };
        }
      }

      // Apply builderProps for fields with company attributes
      if ('companyAttributeId' in field && field.companyAttributeId) {
        const attribute = ctaQuery.data.find((attr) => attr.shortId === field.companyAttributeId);
        if (attribute) {
          const isRequiredSystemAttribute =
            attribute.isSystemAttribute &&
            (attribute.title === 'Full name' || attribute.title === 'Email');
          updatedField.builderProps = {
            ...field.builderProps,
            canEditKind: attributeTypeToAllowedFieldTypes[attribute.attributeType],
            canEditRequired: !isRequiredSystemAttribute,
            canRemove: !isRequiredSystemAttribute,
            canEditConditions: !isRequiredSystemAttribute,
            canDuplicate: !isRequiredSystemAttribute,
          };
        }
      }

      return updatedField;
    };

    return {
      ...form,
      fields: form.fields.map(processField),
    };
  };

  const processedForm = processFormFields(journeyForm.fieldsConfig);

  return (
    <Container>
      <FormBuilder
        formVariant="journey"
        name={journeyForm.name}
        description={journeyForm.description}
        form={processedForm}
        showKennitala={hasKennitalaEnabled}
        submissionMessage={journeyForm.submissionMessageConfig}
        predefinedOptionsLists={lists}
        extraFieldGroups={<JourneyFieldsGroup journeyId={journeyId} />}
        onSave={async (template, onSaveSuccess) => {
          const response = await updateMutation.mutateAsync({
            ...journeyForm,
            ...template,
          });
          onSaveSuccess?.(response.fieldsConfig, response.submissionMessageConfig);
        }}
        onImageSave={async (props) => {
          const response = await createFormTemplateImageQuery.mutateAsync(props);
          return response.imageId;
        }}
        extraTabs={extraTabs}
        extraTopActions={<ExtraTopActions journeyForm={formQuery.data} />}
        secondaryActions={<FormBuilderIsActiveSwitch journeyForm={formQuery.data} />}
        onComplete={onClose}
        useTabs
      />
    </Container>
  );
};

const ExtraTopActions = ({ journeyForm }: { journeyForm: JourneyForm }) => {
  return (
    <>
      <FormActions form={journeyForm} />
      <ShareJourneyFormButton variant="secondary" journeyForm={journeyForm} />
    </>
  );
};

const FormActions = ({ form }: { form: JourneyForm }) => {
  const { t } = useScopedTranslate(locale);

  const shareModal = useShareJourneyFormModal();
  const onShareClick = () => shareModal({ journeyForm: form });
  const showDelete = useDeleteModal();
  const deleteMutation = useDeleteJourneyForm(form);
  const publishMutation = usePublishJourneyForm(form);
  const unpublishMutation = useUnpublishJourneyForm(form);
  const updateMutation = useUpdateJourneyForm(form.shortId);

  const handleToggle = (key: keyof JourneyForm, value: boolean) => {
    updateMutation.mutate({ ...form, [key]: value });
  };

  return (
    <Menu
      buttonProps={{ iconLeft: <IconMoreVertical />, variant: 'secondary' }}
      placement="left-start"
    >
      <FavoriteMenuItem shortId={form.shortId} contentType="journeyform" />
      <Menu.Item
        icon={<IconShare />}
        label={t('share')}
        onSelect={onShareClick}
        isDisabled={!form.isPublished}
      />
      <Menu.Separator />
      <Menu.Switch
        label={t('active')}
        value={form.isPublished}
        onChange={() => {
          return form.isPublished ? unpublishMutation.mutateAsync() : publishMutation.mutateAsync();
        }}
      />

      <Menu.Switch
        label={t('startOnSubmission')}
        value={form.isAutoStart}
        onChange={() => handleToggle('isAutoStart', !form.isAutoStart)}
      />

      <Menu.Separator />
      <Menu.Item
        label={t('core.delete')}
        icon={<IconTrash />}
        variant="danger"
        onSelect={() =>
          showDelete({
            title: t('removeTitle', { name: form.name }),
            description: t('removeDescription', { name: form.name }),
            buttonText: t('core.delete'),
            hasUserConfirmation: true,
            onConfirm: () => {
              deleteMutation.mutateAsync(undefined, { onSuccess: hideOverlay });
            },
          })
        }
      />
    </Menu>
  );
};

const FormBuilderIsActiveSwitch = ({ journeyForm }: { journeyForm: JourneyForm }) => {
  const { t } = useScopedTranslate(locale);
  const publishMutation = usePublishJourneyForm(journeyForm);
  const unpublishMutation = useUnpublishJourneyForm(journeyForm);

  return (
    <Switch
      value={journeyForm.isPublished}
      label={t('active')}
      labelSide="left"
      onChange={() =>
        journeyForm.isPublished ? unpublishMutation.mutate() : publishMutation.mutate()
      }
    />
  );
};

const AddJourneyField = ({ journeyId }: { journeyId: string }) => {
  const { t } = useScopedTranslate(locale);
  const addField = useFormBuilderContext().store.actions.addField;
  const { predefinedOptionsLists } = useFormBuilderContext();
  const showAddAttributeSidebar = useAddAttributeSidebar();
  const createAttributeMutation = useCreateAttribute();
  return (
    <ElementsList.FieldGroup.Element
      label={t('createNew')}
      Icon={IconAddPlus}
      iconColor="elementPurpleInvert"
      color="uiTrans10"
      onClick={() => {
        showAddAttributeSidebar({
          isTravellerAttribute: false,
          onCreate: async (data: JourneyAttributeData) => {
            const attribute = await createAttributeMutation.mutateAsync({
              journeyShortId: journeyId,
              data,
            });
            const kind = attributeTypeToFieldType[attribute.attributeType];
            if (!kind) return;

            const list = predefinedOptionsLists.find(
              (list) => list.value === attribute.options && list.isHidden
            );

            const config = getFieldConfigForKind(kind);
            addField(config.kind as InputKind, {
              label: attribute.title,
              journeyAttributeId: attribute.shortId,
              builderProps: {
                canEditOptions: Boolean(attribute.options && list),
                canEditKind: attributeTypeToAllowedFieldTypes[attribute.attributeType],
              },

              // @ts-expect-error if it isnt a dropdown then it will be undefined anyway
              options: list?.options,
              optionsFromListId: attribute.options,
            });
          },
        });
      }}
    />
  );
};

const JourneyFieldsGroup = ({ journeyId }: { journeyId: string }) => {
  const {
    store: { useFlattenedList },
  } = useFormBuilderContext();
  const { t } = useScopedTranslate(locale);
  const flattened = useFlattenedList(true);
  const ctaQuery = useCompanyTravellerAttributeTemplates({
    systemAttributes: true,
  });
  const jtaQuery = useJourneyAttributes(journeyId);

  if (!ctaQuery.isSuccess || !jtaQuery.isSuccess) {
    return <Spinner />;
  }

  let existingJourneyIds: Array<string | undefined> = [];
  let existingCompanyIds: Array<string | undefined> = [];
  flattened.forEach((field) => {
    if ('journeyAttributeId' in field && field.journeyAttributeId) {
      existingJourneyIds.push(field.journeyAttributeId);
    } else if ('companyAttributeId' in field && field.companyAttributeId) {
      existingCompanyIds.push(field.companyAttributeId);
    }
  });

  const allowedJourneyAttributes = jtaQuery.data.filter((attribute) => {
    return !existingJourneyIds.includes(attribute.shortId) && attribute.isActive;
  });

  const allowedCompanyAttributes = ctaQuery.data.filter((attribute) => {
    return !existingCompanyIds.includes(attribute.shortId) && attribute.isActive;
  });

  return (
    <ElementsList.FieldGroup label={t('fields')}>
      <GroupAccordion label={t('journey')} icon={IconAttributeJourney}>
        <AddJourneyField journeyId={journeyId} />
        {allowedJourneyAttributes.map((attribute) => {
          return (
            <RenderElement
              key={attribute.shortId}
              attribute={attribute}
              journeyAttributeId={attribute.shortId}
            />
          );
        })}
      </GroupAccordion>
      <GroupAccordion label={t('travellerProfile')} icon={IconAttributeTraveller}>
        {allowedCompanyAttributes.map((attribute) => {
          return (
            <RenderElement
              key={attribute.shortId}
              attribute={attribute}
              companyAttributeId={attribute.shortId}
            />
          );
        })}
      </GroupAccordion>
    </ElementsList.FieldGroup>
  );
};

const RenderElement = ({
  attribute,
  journeyAttributeId,
  companyAttributeId,
}: {
  attribute: AttributeType | TravellerAttributeTemplate;
  journeyAttributeId?: string;
  companyAttributeId?: string;
}) => {
  const { predefinedOptionsLists } = useFormBuilderContext();
  const kind = attributeTypeToFieldType[attribute.attributeType];
  if (!kind) {
    return null;
  }

  const config = getFieldConfigForKind(kind);
  const IconToUse = attribute.attributeType === 'decimal' ? IconDecimalFloat : config.Icon;

  // We need to add the "options" locally if we're adding one of the journey form attributes back in
  const list = predefinedOptionsLists.find(
    (list) => list.value === attribute.options && list.isHidden
  );

  let builderProps: BuilderProps = {
    canEditOptions: Boolean(attribute.options && list),
    canEditKind: attributeTypeToAllowedFieldTypes[attribute.attributeType],
  };

  if (kind === 'input-file') {
    builderProps.canEditFilesMultiple = false;
  }

  return (
    <ElementsList.FieldGroup.AddElement
      kind={config.kind}
      dragId={attribute.shortId}
      key={attribute.shortId}
      createProps={{
        label: attribute.title,
        journeyAttributeId,
        companyAttributeId,
        builderProps,

        ...(kind === 'input-file' && {
          allowMultiple: attribute.attributeType === 'file_multi',
        }),
        ...(kind === 'input-number' && {
          isDecimal: attribute.attributeType === 'decimal',
        }),

        // @ts-expect-error if it isnt a dropdown then it will be undefined anyway
        options: list?.options,
        optionsFromListId: attribute.options,
      }}
      Icon={(props) => <IconToUse {...props} color="elementOrange200" />}
      label={attribute.title}
      color="orangeTrans10"
    />
  );
};

// Define which field types are compatible for each attribute type
const attributeTypeToAllowedFieldTypes: Record<AttributeFieldType, FieldTypeKind[]> = {
  single_select: ['input-dropdown', 'input-single-choice'],
  multi_select: [],
  text: [],
  paragraph: [],
  phone_number: [],
  email: [],
  date: [],
  file: [],
  file_multi: [],
  boolean: [],
  user: [],
  decimal: [],
  number: [],
};

const Container = styled(Stack)({
  position: 'fixed',
  top: pxToRem(24),
  left: pxToRem(24),
  right: pxToRem(24),
  bottom: pxToRem(24),
  backgroundColor: theme.color.backgroundLightDark,
  borderRadius: pxToRem(16),
  boxShadow: theme.shadow.fancy,
  overflow: 'hidden',
});

export const locale = {
  en: {
    settings: 'Settings',
    active: 'Active',
    portal: 'Portal',
    createNew: 'Create new',
    fields: 'Fields',
    journey: 'Journey',
    travellerProfile: 'Traveller profile',
    addToFavourites: 'Add to favourites',
    startOnSubmission: 'Start on submission',
    autoStartEnabled: 'Auto start enabled',
    autoStartDisabled: 'Auto start disabled',
    removeTitle: 'Delete external form',
    removeDescription: 'Are you sure you want to delete this external form?',
    unsavedChangesTitle: 'Unsaved Changes',
    unsavedChangesDescription: 'You have unsaved changes. Do you want to save them?',
    saveForm: 'Save Form',
    share: 'Share',
    formNotFound: 'Form not found. It may have been deleted.',
  },
};
